import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AccessNotice } from "@/components/PageStates";
import { cn } from "@/lib/utils";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowUpRight,
  ChevronLeft,
  ClipboardList,
  FileText,
  FolderTree,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  ShoppingCart,
  Truck,
  UserCircle2,
  Wallet,
  X,
} from "lucide-react";
import {
  BARANG_KELUAR_ROLES,
  MANAGEMENT_ROLES,
  MODAL_PAGE_ROLES,
  REPORT_PAGE_ROLES,
  ROLE_KEPALA_RUANGAN,
  ROLE_USER,
  SUPPLIER_PAGE_ROLES,
} from "@/lib/roles";

const SIDEBAR_COLLAPSED_KEY = "project-dapur.sidebar-collapsed";

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
    } catch {
      // Ignore storage errors.
    }
  }, [sidebarCollapsed]);

  const standalonePaths = ["/barang-masuk", "/barang-keluar", "/pemesanan"];
  const viewMode = new URLSearchParams(location.search).get("view");
  const isStandaloneView =
    viewMode === "standalone" && standalonePaths.includes(location.pathname);
  const accessDenied = location.state?.accessDenied;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const menuItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: [ROLE_KEPALA_RUANGAN] },
    { path: "/master-barang", label: "Master Barang", icon: Package, roles: MANAGEMENT_ROLES },
    { path: "/master-kategori", label: "Master Kategori", icon: FolderTree, roles: [ROLE_KEPALA_RUANGAN] },
    { path: "/master-supplier", label: "Master Supplier", icon: Truck, roles: SUPPLIER_PAGE_ROLES },
    { path: "/barang-masuk", label: "Barang Masuk", icon: ArrowDownToLine, roles: MANAGEMENT_ROLES, openInNewTab: true },
    { path: "/barang-keluar", label: "Barang Keluar", icon: ArrowUpFromLine, roles: BARANG_KELUAR_ROLES, openInNewTab: true },
    { path: "/pemesanan", label: "Pemesanan Barang", icon: ShoppingCart, roles: MANAGEMENT_ROLES, openInNewTab: true },
    { path: "/modal", label: "Modal", icon: Wallet, roles: MODAL_PAGE_ROLES },
    { path: "/laporan", label: "Laporan", icon: FileText, roles: REPORT_PAGE_ROLES },
    { path: "/rab", label: "RAB", icon: ClipboardList, roles: MANAGEMENT_ROLES },
    { path: "/user-management", label: "Manajemen User", icon: Menu, roles: [ROLE_KEPALA_RUANGAN] },
    { path: "/profile", label: "Profil Saya", icon: UserCircle2, roles: MANAGEMENT_ROLES },
  ];

  const filteredMenuItems = menuItems.filter((item) => item.roles.includes(user?.role));
  const sidebarCollapseLabel = sidebarCollapsed
    ? "Buka menu dashboard"
    : "Minimalkan menu dashboard";

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const openInNewTab = item.openInNewTab && user?.role !== ROLE_USER;
    const targetPath = openInNewTab ? `${item.path}?view=standalone` : item.path;
    const label = openInNewTab ? `${item.label} (Tab baru)` : item.label;

    const link = (
      <Link
        key={item.path}
        to={targetPath}
        target={openInNewTab ? "_blank" : undefined}
        rel={openInNewTab ? "noopener noreferrer" : undefined}
        aria-current={isActive ? "page" : undefined}
        aria-label={label}
        data-testid={`nav-${item.path.substring(1)}`}
        onClick={() => setSidebarOpen(false)}
        className={cn(
          "group flex items-center rounded-2xl font-manrope font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
          sidebarCollapsed
            ? "justify-between gap-3 px-4 py-3 md:justify-center md:px-3"
            : "justify-between gap-3 px-4 py-3",
          isActive
            ? "bg-primary text-primary-foreground shadow-sm"
            : "text-foreground hover:bg-accent/80"
        )}
      >
        <span
          className={cn(
            "flex min-w-0 items-center gap-3",
            sidebarCollapsed && "md:justify-center md:gap-0"
          )}
        >
          <Icon size={20} className="shrink-0" />
          <span className={cn("truncate", sidebarCollapsed && "md:hidden")}>{item.label}</span>
        </span>
        {openInNewTab ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]",
              isActive
                ? "bg-white/15 text-primary-foreground/90"
                : "bg-slate-100 text-slate-500 group-hover:bg-white",
              sidebarCollapsed && "md:hidden"
            )}
          >
            <ArrowUpRight size={12} />
            Tab baru
          </span>
        ) : null}
      </Link>
    );

    if (!sidebarCollapsed) {
      return link;
    }

    return (
      <Tooltip key={item.path}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" className="max-w-[220px]">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  };

  if (isStandaloneView) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.08),_transparent_38%),linear-gradient(180deg,_rgba(248,250,252,0.92)_0%,_rgba(255,255,255,1)_100%)]">
        <main className="min-h-screen">
          <div className="mx-auto max-w-7xl p-6 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={120}>
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.08),_transparent_38%),linear-gradient(180deg,_rgba(248,250,252,0.92)_0%,_rgba(255,255,255,1)_100%)]">
        <div className="fixed left-0 right-0 top-0 z-50 border-b border-border/50 bg-white/85 backdrop-blur-md md:hidden">
          <div className="flex items-center justify-between p-4">
            <div>
              <h1 className="font-manrope text-lg font-bold text-primary">Inventory Dapur RS</h1>
              <p className="text-xs text-muted-foreground">{user?.role}</p>
            </div>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="rounded-xl border border-border/70 bg-white p-2 text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label={sidebarOpen ? "Tutup menu navigasi" : "Buka menu navigasi"}
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-40 flex w-72 flex-col overflow-hidden border-r border-border/50 bg-white/95 shadow-xl shadow-slate-200/60 backdrop-blur transition-all duration-300",
            sidebarOpen ? "translate-x-0" : "-translate-x-full",
            sidebarCollapsed ? "md:w-24" : "md:w-72",
            "md:translate-x-0"
          )}
        >
          <div
            className={cn(
              "border-b border-border/50 px-6 py-6",
              sidebarCollapsed && "md:px-3 md:py-4"
            )}
          >
            <div
              className={cn(
                "flex items-center gap-3",
                sidebarCollapsed ? "md:justify-center" : "md:justify-between"
              )}
            >
              <div className={cn("min-w-0", sidebarCollapsed && "md:hidden")}>
                <h1 className="font-manrope text-xl font-extrabold tracking-tight text-primary">
                  Inventory Dapur RS
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">{user?.role}</p>
              </div>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    data-testid="sidebar-collapse-toggle"
                    onClick={() => setSidebarCollapsed((value) => !value)}
                    variant={sidebarCollapsed ? "default" : "outline"}
                    className={cn(
                      "hidden shrink-0 rounded-2xl md:inline-flex",
                      sidebarCollapsed
                        ? "h-12 w-12 shadow-md shadow-emerald-950/10"
                        : "h-10 w-10 bg-white/90"
                    )}
                    aria-label={sidebarCollapseLabel}
                  >
                    {sidebarCollapsed ? <LayoutDashboard size={20} /> : <ChevronLeft size={20} />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">{sidebarCollapseLabel}</TooltipContent>
              </Tooltip>
            </div>
          </div>

          <ScrollArea className={cn("flex-1 py-4", sidebarCollapsed ? "px-2" : "px-3")}>
            <nav className="space-y-1">{filteredMenuItems.map(renderMenuItem)}</nav>
          </ScrollArea>

          <div className={cn("border-t border-border/50", sidebarCollapsed ? "p-2" : "p-4")}>
            <div
              className={cn(
                "mb-3 rounded-2xl bg-slate-50 px-4 py-3",
                sidebarCollapsed && "md:hidden"
              )}
            >
              <p className="text-sm font-manrope font-semibold text-foreground">
                {user?.username}
              </p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>

            {sidebarCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="logout-button"
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full rounded-2xl md:mx-auto md:h-12 md:w-12 md:px-0"
                    aria-label="Keluar"
                  >
                    <LogOut size={16} className="shrink-0 md:mr-0" />
                    <span className="md:hidden">Keluar</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Keluar</TooltipContent>
              </Tooltip>
            ) : (
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="outline"
                className="w-full rounded-2xl"
              >
                <LogOut size={16} className="mr-2" />
                Keluar
              </Button>
            )}
          </div>
        </aside>

        <main
          className={cn(
            "min-h-screen pt-20 transition-[margin-left] duration-300 md:pt-0",
            sidebarCollapsed ? "md:ml-24" : "md:ml-72"
          )}
        >
          <div className="px-4 pb-8 pt-4 md:px-8 md:py-8">
            {accessDenied ? (
              <div className="mx-auto mb-6 w-full max-w-7xl">
                <AccessNotice
                  role={user?.role}
                  requestedPath={accessDenied.requestedPath}
                  requiredRoles={accessDenied.requiredRoles}
                />
              </div>
            ) : null}
            <Outlet />
          </div>
        </main>

        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
    </TooltipProvider>
  );
};

export default Layout;
